import { ReplicationRow } from '@esg/esg-global-types';
import {
  BaseFilters,
  GetReplicationRowsRequest,
  ReplictionRowRequestMetadata,
  parseResolverResponse
} from '@esg/syntiro-api-core';
import { DocumentNode } from 'graphql';
import { validateParams } from '../validators/base.validator';
import { isValidGroupId } from '../validators/group.validator';
import { isValidCompanyId } from '../validators/company.validator';
import { apollo_client_v2 } from '../../App';
import { FetchResult } from '@apollo/client';
import { generateUuidV4 } from '../../util/uuid';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { MetadataError } from '@ep/error-handling';
import { GET_REPLICATION_ROWS } from '../queries/replication.queries';
import { isValidReplicationId, isValidTableId } from '../validators/replication.validator';

const SOURCE = 'replication.service.ts';

/**
 * Query all replication row documents for current portal company.
 * @param {ResolverQueryRequestMetadata} metadata Metadata for request containing group and company data.
 * @param {MetricRecordFilters} filters Filter parameters to use for metric records query.
 * @param {DocumentNode | undefined} query Optionally pass in specific query to use for desired metric record fields.
 * @returns {Promise<Array<MetricRecord>>}
 */
export const getReplicationRows = async (
  metadata: ReplictionRowRequestMetadata,
  filters: BaseFilters,
  query: DocumentNode = GET_REPLICATION_ROWS
): Promise<Array<ReplicationRow>> => {
  validateParams(
    { metadata },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      },
      'metadata.replication_id': {
        validate: isValidReplicationId,
        message: 'Invalid replication_id specified.'
      },
      'metadata.table_id': {
        validate: isValidTableId,
        message: 'Invalid table_id specified.'
      }
    }
  );
  const input: GetReplicationRowsRequest['input'] = {
    metadata: metadata,
    filter: filters
  };
  return apollo_client_v2
    .query({
      variables: { input },
      query: query,
      fetchPolicy: 'no-cache'
    })
    .then((response: FetchResult) => {
      if (!response.data?.getReplicationRows) {
        throw new Error('No data returned');
      }
      const api_result: Array<ReplicationRow> | null | undefined = parseResolverResponse<
        Array<ReplicationRow>
      >(response.data.getReplicationRows);
      return api_result ? api_result : [];
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getReplicationRows'),
        { metadata, filters, query },
        tracking_id
      );
    });
};
